import { useState } from "react";

/**
 * AdminPanel
 * Internal ops/support view — gate this route behind an admin role check
 * upstream of mounting this component.
 * Props:
 *   metrics — { totalCustomers, feedsTracked, queueDepth, failedDeliveries1h }
 *   customers — [{ id, account, plan, projectCount, feedCount, status: 'ok'|'past_due'|'suspended' }]
 *   onSearch(query)
 *   onOpenCustomer(id)
 */
export default function AdminPanel({ metrics, customers = [], onSearch, onOpenCustomer }) {
  const [query, setQuery] = useState("");

  function statusBadge(status) {
    if (status === "ok") return { cls: "ical-badge-success", label: "OK" };
    if (status === "past_due") return { cls: "ical-badge-warning", label: "Past due" };
    return { cls: "ical-badge-danger", label: "Suspended" };
  }

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16, color: "var(--text-warning)" }}>
        <span style={{ fontSize: "var(--text-xs)" }}>⚠ Internal — ops/support only</span>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 12, marginBottom: 20 }}>
        <MetricCard label="Total customers" value={metrics?.totalCustomers} />
        <MetricCard label="Feeds tracked" value={metrics?.feedsTracked} />
        <MetricCard label="Poll queue depth" value={metrics?.queueDepth} />
        <MetricCard label="Failed deliveries (1h)" value={metrics?.failedDeliveries1h} danger />
      </div>

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
        <p style={{ fontWeight: 500, fontSize: "var(--text-base)", margin: 0 }}>Customers</p>
        <input
          className="ical-input"
          type="search"
          placeholder="Search by email or account ID"
          style={{ width: 220 }}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            onSearch?.(e.target.value);
          }}
        />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 90px 80px 80px 90px 32px", gap: 12, padding: "6px 12px", fontSize: "var(--text-xs)", color: "var(--text-muted)" }}>
        <span>Account</span><span>Plan</span><span>Projects</span><span>Feeds</span><span>Status</span><span />
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {customers.map((c) => {
          const badge = statusBadge(c.status);
          return (
            <div
              key={c.id}
              className="ical-row"
              style={{ display: "grid", gridTemplateColumns: "1fr 90px 80px 80px 90px 32px", alignItems: "center", gap: 12, cursor: "pointer" }}
              onClick={() => onOpenCustomer?.(c.id)}
            >
              <span style={{ fontSize: "var(--text-sm)" }}>{c.account}</span>
              <span style={{ fontSize: "var(--text-sm)" }}>{c.plan}</span>
              <span style={{ fontSize: "var(--text-sm)" }}>{c.projectCount?.toLocaleString()}</span>
              <span style={{ fontSize: "var(--text-sm)" }}>{c.feedCount?.toLocaleString()}</span>
              <span className={`ical-badge ${badge.cls}`}>{badge.label}</span>
              <span style={{ color: "var(--text-muted)" }}>⋯</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function MetricCard({ label, value, danger }) {
  return (
    <div className="ical-card">
      <p style={{ fontSize: "var(--text-xs)", color: "var(--text-secondary)", margin: "0 0 6px" }}>{label}</p>
      <p style={{ fontSize: "var(--text-2xl)", fontWeight: 500, margin: 0, color: danger && value > 0 ? "var(--text-danger)" : "var(--text-primary)" }}>
        {value?.toLocaleString?.() ?? value}
      </p>
    </div>
  );
}
